import {
  createRrWindow,
  gsrSamplesToCsv,
  interpretGsrRangeMeasurement,
  isUsableGsrMeasurement,
  summarizeGsrQuality,
  type GsrBatteryStatus,
  type GsrQualitySummary,
  type GsrRangeMeasurement,
  type GsrSample,
} from '@versports/ble-contracts'
import { tonicPhasic, trimWindow, type ConductanceSample } from './gsrTonicPhasic'

/**
 * Estado "En vivo" de un sensor, SIN importar por dónde llegan las muestras.
 *
 * Hasta el 2026-09-17 este cálculo vivía adentro de `usePolarDirect` /
 * `useGsrDirect` (Web Bluetooth a este equipo), y los sensores del kit —los
 * que reporta la Raspberry por el relay— sólo se veían como JSON crudo en la
 * `BleDeviceCard`. Pedido de mg: *"unificá esta interfaz para que use lo mismo
 * que se usa abajo en conexión directa por BLE"*. Los trackers de acá reciben
 * mediciones ya decodificadas (una notificación GATT o un snapshot de la Pi)
 * y devuelven el mismo `live` que dibuja `SensorLivePanel`.
 *
 * Son objetos mutables con estado interno (ventanas, buffers) a propósito: la
 * ventana de RR y el buffer crudo del GSR son miles de valores que no tienen
 * que copiarse en cada notificación; el `live` que sale sí es inmutable y
 * apto para `setState`.
 */

/** Cuántos intervalos entre notifies se guardan para medir la cadencia. */
const INTERVAL_WINDOW = 30
/** Muestras guardadas para las trazas del Polar (a ~1 Hz son ~3 min de HR). */
const POLAR_TRACE_LENGTH = 180
/** Muestras guardadas para las trazas del GSR (a 10 Hz son 30 s). */
const GSR_TRACE_LENGTH = 300
/** Buffer crudo del GSR para calidad y CSV (a 10 Hz son 10 min). */
const SAMPLE_BUFFER_MAX = 6000
/** Cada cuántas muestras se recomputa el resumen de calidad. */
const QUALITY_REFRESH_EVERY = 10

function pushTrace(trace: number[], value: number, max: number): number[] {
  const next = trace.length >= max ? trace.slice(1) : trace.slice()
  next.push(value)
  return next
}

function median(values: number[]): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2
}

/* -------------------------------------------------------------------------- */
/* Polar H10                                                                  */
/* -------------------------------------------------------------------------- */

export interface PolarLive {
  hr: number | null
  rmssdMs: number | null
  /** Último notify: RR crudos en ms tal como vinieron. */
  lastRrMs: number[]
  notifies: number
  rrCount: number
  /** Intervalo mediano entre notifies (ms). */
  notifyIntervalMs: number | null
  lastNotifyAt: number | null
  startedAt: number | null
  /** Trazas para los sparklines, más viejo primero. */
  hrTrace: number[]
  rrTrace: number[]
}

export const EMPTY_POLAR_LIVE: PolarLive = {
  hr: null,
  rmssdMs: null,
  lastRrMs: [],
  notifies: 0,
  rrCount: 0,
  notifyIntervalMs: null,
  lastNotifyAt: null,
  startedAt: null,
  hrTrace: [],
  rrTrace: [],
}

export class PolarLiveTracker {
  live: PolarLive = EMPTY_POLAR_LIVE
  private rrWindow = createRrWindow()
  private intervals: number[] = []

  /** Vacía trazas, ventana de RR y contadores. */
  reset(now: number = Date.now()): PolarLive {
    this.rrWindow.reset()
    this.intervals = []
    this.live = { ...EMPTY_POLAR_LIVE, startedAt: now }
    return this.live
  }

  /** Una medición de HR (0 = sin lectura) con sus RR en ms. */
  feed(hr: number, rrMs: number[], now: number = Date.now()): PolarLive {
    const prev = this.live
    const window = this.rrWindow.feed(rrMs, now)
    if (prev.lastNotifyAt !== null) {
      this.intervals.push(now - prev.lastNotifyAt)
      if (this.intervals.length > INTERVAL_WINDOW) this.intervals.shift()
    }
    let rrTrace = prev.rrTrace
    for (const rr of rrMs) rrTrace = pushTrace(rrTrace, rr, POLAR_TRACE_LENGTH)
    this.live = {
      hr: hr > 0 ? hr : prev.hr,
      rmssdMs: window.rmssdMs,
      lastRrMs: rrMs,
      notifies: prev.notifies + 1,
      rrCount: prev.rrCount + rrMs.length,
      notifyIntervalMs: median(this.intervals),
      lastNotifyAt: now,
      startedAt: prev.startedAt ?? now,
      hrTrace: hr > 0 ? pushTrace(prev.hrTrace, hr, POLAR_TRACE_LENGTH) : prev.hrTrace,
      rrTrace,
    }
    return this.live
  }
}

/* -------------------------------------------------------------------------- */
/* GSR (placa propia)                                                         */
/* -------------------------------------------------------------------------- */

export interface GsrLive {
  /** SCL tónico reportado por la Pico (characteristic `scl`), en µS. */
  sclUS: number | null
  /** SCR reportado por la Pico (characteristic `scr`), en µS. */
  scrUS: number | null
  /** Última medición cruda del `counter`, ya convertida a resistencia. */
  measurement: GsrRangeMeasurement | null
  /** ms desde el último cambio de rango: por debajo del settle (110 ms) la
   * muestra todavía no asentó. */
  elapsedSinceRangeChangeMs: number | null
  /** Hora de medición en el reloj de la Pico (firmware >= 2026-09-07), o null
   * con el payload de 9 bytes. */
  sampleTimeMs: number | null
  /** Tónico calculado acá: mediana de las muestras válidas de los últimos 30 s
   * (µS). null hasta juntar 20 muestras válidas. */
  tonicUS: number | null
  /** Fásico calculado acá: última muestra válida − tónico, con signo (µS). */
  phasicUS: number | null
  /** Muestras válidas que sostienen el tónico. */
  tonicWindowSamples: number
  battery: GsrBatteryStatus | null
  notifies: number
  /** Intervalo mediano entre notifies del `counter` (ms). */
  notifyIntervalMs: number | null
  lastNotifyAt: number | null
  startedAt: number | null
  /** Trazas para los sparklines, más viejo primero. */
  sclTrace: number[]
  conductanceTrace: number[]
  adcTrace: number[]
  /** Cuántas de las últimas muestras del `counter` salieron saturadas o en cero. */
  invalidSamples: number
  totalSamples: number
  /** Resumen de calidad sobre el buffer crudo (cadencia real, ruido, gaps,
   * saturación, golpeteo de rango). Se recomputa cada `QUALITY_REFRESH_EVERY`. */
  quality: GsrQualitySummary | null
}

export const EMPTY_GSR_LIVE: GsrLive = {
  sclUS: null,
  scrUS: null,
  measurement: null,
  elapsedSinceRangeChangeMs: null,
  sampleTimeMs: null,
  tonicUS: null,
  phasicUS: null,
  tonicWindowSamples: 0,
  battery: null,
  notifies: 0,
  notifyIntervalMs: null,
  lastNotifyAt: null,
  startedAt: null,
  sclTrace: [],
  conductanceTrace: [],
  adcTrace: [],
  invalidSamples: 0,
  totalSamples: 0,
  quality: null,
}

/** Una lectura del `counter` ya parseada (Web Bluetooth) o ya interpretada por
 * la Pi (snapshot): lo mínimo para reconstruir la medición acá. */
export interface GsrCounterReading {
  /** null = payload legacy sin medición de rango: sólo cuenta como notify. */
  rangeId: number | null
  adcCode: number
  elapsedSinceRangeChangeMs: number | null
  sampleTimeMs: number | null
}

export class GsrLiveTracker {
  live: GsrLive = EMPTY_GSR_LIVE
  private intervals: number[] = []
  private window: ConductanceSample[] = []
  private samples: GsrSample[] = []

  /** Vacía trazas, ventana del tónico y contadores; conserva la batería. */
  reset(now: number = Date.now()): GsrLive {
    this.intervals = []
    this.window = []
    this.samples = []
    this.live = { ...EMPTY_GSR_LIVE, battery: this.live.battery, startedAt: now }
    return this.live
  }

  feedScl(value: number): GsrLive {
    this.live = {
      ...this.live,
      sclUS: value,
      sclTrace: pushTrace(this.live.sclTrace, value, GSR_TRACE_LENGTH),
    }
    return this.live
  }

  feedScr(value: number): GsrLive {
    this.live = { ...this.live, scrUS: value }
    return this.live
  }

  feedBattery(battery: GsrBatteryStatus): GsrLive {
    this.live = { ...this.live, battery }
    return this.live
  }

  feedCounter(reading: GsrCounterReading, now: number = Date.now()): GsrLive {
    const prev = this.live
    if (prev.lastNotifyAt !== null) {
      this.intervals.push(now - prev.lastNotifyAt)
      if (this.intervals.length > INTERVAL_WINDOW) this.intervals.shift()
    }
    const base: GsrLive = {
      ...prev,
      notifies: prev.notifies + 1,
      notifyIntervalMs: median(this.intervals),
      lastNotifyAt: now,
      startedAt: prev.startedAt ?? now,
    }
    if (reading.rangeId === null) {
      this.live = base
      return this.live
    }
    const measurement = interpretGsrRangeMeasurement(reading.rangeId, reading.adcCode)
    const valid = isUsableGsrMeasurement(measurement, reading.elapsedSinceRangeChangeMs)
    if (valid) this.window.push({ t: now, uS: measurement.conductanceUS! })
    this.window = trimWindow(this.window, now)
    const decomposed = tonicPhasic(this.window)
    // Buffer crudo para el resumen de calidad y el CSV: miles de filas que
    // sólo se leen al recomputar el resumen o al exportar.
    this.samples.push({
      t: now,
      rangeId: reading.rangeId,
      adcCode: measurement.adcCode,
      quality: measurement.quality,
      conductanceUS: measurement.conductanceUS,
      elapsedSinceRangeChangeMs: reading.elapsedSinceRangeChangeMs,
      sclUS: prev.sclUS,
    })
    if (this.samples.length > SAMPLE_BUFFER_MAX) this.samples.shift()
    this.live = {
      ...base,
      quality:
        this.samples.length % QUALITY_REFRESH_EVERY === 0
          ? summarizeGsrQuality(this.samples)
          : prev.quality,
      measurement,
      elapsedSinceRangeChangeMs: reading.elapsedSinceRangeChangeMs,
      sampleTimeMs: reading.sampleTimeMs,
      tonicUS: decomposed.tonicUS,
      phasicUS: decomposed.phasicUS,
      tonicWindowSamples: decomposed.windowSamples,
      totalSamples: prev.totalSamples + 1,
      invalidSamples: prev.invalidSamples + (valid ? 0 : 1),
      adcTrace: pushTrace(prev.adcTrace, measurement.adcCode, GSR_TRACE_LENGTH),
      // Una muestra saturada NO entra a la traza de conductancia: no hay
      // número real que graficar y una línea plana en el tope mentiría
      // sobre la señal (mismo criterio que la Pi, que no fabrica
      // resistencia fuera del rango válido).
      conductanceTrace: valid
        ? pushTrace(prev.conductanceTrace, measurement.conductanceUS!, GSR_TRACE_LENGTH)
        : prev.conductanceTrace,
    }
    return this.live
  }

  get sampleCount(): number {
    return this.samples.length
  }

  /** Buffer crudo como CSV (una fila por notificación), o null si está vacío. */
  csv(): string | null {
    return this.samples.length ? gsrSamplesToCsv(this.samples) : null
  }
}
